import React, { useState, useEffect, useRef } from 'react';
import './PayPalButton.css';

interface PayPalButtonProps {
  planId: string;
  tierName: string;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

// PayPal SDK types
declare global {
  interface Window {
    paypal?: {
      Buttons: (config: PayPalButtonConfig) => {
        render: (container: string) => Promise<void>;
      };
    };
  }
}

interface PayPalButtonConfig {
  style?: {
    layout?: 'vertical' | 'horizontal';
    color?: 'gold' | 'blue' | 'silver' | 'white' | 'black';
    shape?: 'rect' | 'pill';
    label?: 'paypal' | 'checkout' | 'buynow' | 'pay' | 'subscribe';
  };
  createSubscription: (data: any, actions: any) => Promise<string>;
  onApprove: (data: any, actions: any) => Promise<void>;
  onError?: (err: any) => void;
  onCancel?: (data: any) => void;
}

const PayPalButton: React.FC<PayPalButtonProps> = ({
  planId,
  tierName,
  onSuccess,
  onError
}) => {
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [scriptError, setScriptError] = useState<string | null>(null);
  const [isRendering, setIsRendering] = useState(false);
  const buttonContainerRef = useRef<HTMLDivElement>(null);
  const scriptRef = useRef<HTMLScriptElement | null>(null);

  // CloudFront domain for return URL
  const CLOUDFRONT_DOMAIN = 'https://dn9souybgk5tq.cloudfront.net';
  const RETURN_URL = `${CLOUDFRONT_DOMAIN}/subscription-return`;

  // PayPal Client ID - in production, this should come from environment variables
  // For now, using a placeholder that should be replaced with actual client ID
  const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID || 'YOUR_PAYPAL_CLIENT_ID';

  useEffect(() => {
    // Check if script is already loaded
    if (window.paypal) {
      setScriptLoaded(true);
      return;
    }

    // Check if script is already being loaded
    const existingScript = document.querySelector('script[src*="paypal.com/sdk/js"]');
    if (existingScript) {
      existingScript.addEventListener('load', () => setScriptLoaded(true));
      existingScript.addEventListener('error', () => {
        setScriptError('Failed to load PayPal SDK');
      });
      return;
    }

    // Load PayPal SDK script
    const script = document.createElement('script');
    script.src = `https://www.paypal.com/sdk/js?client-id=${PAYPAL_CLIENT_ID}&vault=true&intent=subscription`;
    script.async = true;
    
    script.addEventListener('load', () => {
      setScriptLoaded(true);
      setScriptError(null);
    });
    
    script.addEventListener('error', () => {
      setScriptError('Failed to load PayPal SDK. Please check your internet connection and try again.');
      console.error('PayPal SDK failed to load');
    });

    document.body.appendChild(script);
    scriptRef.current = script;

    // Cleanup function
    return () => {
      // Note: We don't remove the script on unmount because other components might be using it
      // The script will be cleaned up when the page is unloaded
    };
  }, [PAYPAL_CLIENT_ID]);

  useEffect(() => {
    if (!scriptLoaded || !window.paypal || isRendering || !buttonContainerRef.current) {
      return;
    }

    // Clear any existing buttons
    if (buttonContainerRef.current) {
      buttonContainerRef.current.innerHTML = '';
    }

    setIsRendering(true);

    try {
      // Render PayPal button
      window.paypal.Buttons({
        style: {
          layout: 'vertical',
          color: 'gold',
          shape: 'rect',
          label: 'subscribe'
        },
        createSubscription: async (data, actions) => {
          try {
            console.log('Creating subscription with plan ID:', planId);
            const subscriptionId = await actions.subscription.create({
              plan_id: planId,
              application_context: {
                return_url: RETURN_URL,
                cancel_url: CLOUDFRONT_DOMAIN
              }
            });
            console.log('Subscription created successfully:', subscriptionId);
            // Save planId so ReturnHandler can call initializeSubscription
            // if PayPal redirects the full page (PayPal account flow bypasses onApprove)
            sessionStorage.setItem('pendingPlanId', planId);
            sessionStorage.setItem('pendingSubscriptionId', subscriptionId);
            return subscriptionId;
          } catch (error) {
            console.error('Error creating subscription:', error);
            throw error;
          }
        },
        onApprove: async (data, actions) => {
          try {
            console.log('Subscription approved:', data);
            
            // Initialize subscription record in backend
            try {
              const { apiService } = await import('../../services/api');
              await apiService.initializeSubscription(data.subscriptionID, planId);
              console.log('Subscription record initialized in backend');
            } catch (initError) {
              console.error('Failed to initialize subscription record:', initError);
              // Continue anyway - webhook will handle it
            }
            
            // Call success callback if provided
            if (onSuccess) {
              onSuccess();
            }
            
            // Redirect to return URL with subscription ID
            window.location.href = `${RETURN_URL}?subscription_id=${data.subscriptionID}`;
          } catch (error) {
            console.error('Error in onApprove:', error);
            const err = error instanceof Error ? error : new Error('Payment approval failed');
            if (onError) {
              onError(err);
            }
          }
        },
        onError: (err) => {
          console.error('PayPal button error:', err);
          const error = new Error('Payment processing failed. Please try again.');
          if (onError) {
            onError(error);
          }
        },
        onCancel: (data) => {
          console.log('Payment cancelled by user:', data);
          // User cancelled the payment, no action needed
        }
      }).render('#paypal-button-container-' + planId)
        .then(() => {
          setIsRendering(false);
        })
        .catch((error) => {
          console.error('Failed to render PayPal button:', error);
          setScriptError('Failed to render payment button. Please refresh the page.');
          setIsRendering(false);
        });
    } catch (error) {
      console.error('Error setting up PayPal button:', error);
      setScriptError('Failed to initialize payment button. Please refresh the page.');
      setIsRendering(false);
    }
  }, [scriptLoaded, planId, tierName, onSuccess, onError, RETURN_URL, CLOUDFRONT_DOMAIN]);

  if (scriptError) {
    return (
      <div className="paypal-button-error">
        <p className="error-message">{scriptError}</p>
        <button 
          className="retry-button"
          onClick={() => {
            setScriptError(null);
            window.location.reload();
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  if (!scriptLoaded) {
    return (
      <div className="paypal-button-loading">
        <div className="loading-spinner"></div>
        <p>Loading payment options...</p>
      </div>
    );
  }

  return (
    <div className="paypal-button-wrapper">
      <div 
        id={`paypal-button-container-${planId}`}
        ref={buttonContainerRef}
        className="paypal-button-container"
      />
    </div>
  );
};

export default PayPalButton;
